import React, { useEffect } from 'react';
import { Toast as ToastType, useToast } from '@/hooks/useToast';

const Toast: React.FC<{ toast: ToastType; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
  // Get icon and styles based on toast type
  const getTypeStyles = (type: ToastType['type']) => {
    switch (type) {
      case 'success':
        return {
          containerClass: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
          iconClass: 'text-green-500',
          icon: '✓'
        };
      case 'error':
        return {
          containerClass: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
          iconClass: 'text-red-500',
          icon: '✕'
        };
      case 'warning':
        return {
          containerClass: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800',
          iconClass: 'text-yellow-500',
          icon: '⚠'
        };
      case 'info':
      default:
        return {
          containerClass: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800',
          iconClass: 'text-blue-500',
          icon: 'ℹ'
        };
    }
  };

  // Handle closing toast
  const handleClose = () => {
    onRemove(toast.id);
  };

  // Auto remove after duration if specified
  useEffect(() => {
    if (toast.duration) {
      const timer = setTimeout(() => {
        onRemove(toast.id);
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast, onRemove]);

  const styles = getTypeStyles(toast.type);

  return (
    <div
      className={`flex items-start p-4 mb-2 rounded-lg shadow-md border ${styles.containerClass} animate-fadeIn`}
      role="alert"
    >
      <div className={`inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg ${styles.iconClass}`}>
        <span className="text-xl">{styles.icon}</span>
      </div>
      <div className="ml-3 text-sm font-normal w-full">
        <div className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">{toast.title}</div>
        {toast.description && (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {toast.description}
          </div>
        )}
      </div>
      <button
        type="button"
        className="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 inline-flex items-center justify-center h-8 w-8 text-gray-400 hover:text-gray-900 dark:hover:text-white"
        onClick={handleClose}
        aria-label="Close"
      >
        <span className="sr-only">Close</span>
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, dismissToast } = useToast();

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md">
      {toasts.map(toast => (
        <Toast 
          key={toast.id} 
          toast={toast} 
          onRemove={dismissToast} 
        />
      ))}
    </div>
  );
};

export default ToastContainer; 